/* istanbul ignore file */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAzureAuthenticator from 'infrastructure/security/authentication/azure/azureAuthenticator';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import CurrentUserAvatarField from '../CurrentUserAvatarField/CurrentUserAvatarField';
import { statusConfig, StatusType } from 'domain/people/availability/availabilityUser/constants';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { useAvailabilityCurrentUser } from 'domain/people/availability/availabilityUser/AvailabilityCurrentUserProvider';

const orderedStatuses: StatusType[] = [
  StatusType.FOCUSED,
  StatusType.WORKING_INTERRUPTIBLE,
  StatusType.NON_WORKING_INTERRUPTIBLE,
  StatusType.OFFLINE,
];

const STATUS_ICON_SX = {
  width: 12,
  height: 12,
  flexShrink: 0,
} as const;

const MENU_ICON_SX = {
  minWidth: 28,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
} as const;

const MENU_ITEM_SX = {
  minHeight: 34,
  py: 1,
  fontSize: 14,
} as const;

const STATUS_SUBMENU_PAPER_SX = {
  minWidth: 200,
  borderRadius: 1,
  boxShadow: 3,
} as const;

const MENU_SLOT_PROPS = {
  paper: {
    sx: {
      minWidth: 200,
    },
  },
  list: {
    sx: {
      p: 0,
    },
  },
} as const;

const STATUS_SUBMENU_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, -4],
    },
  },
];

const CurrentUserActions = ({ userName }: { userName?: Dictionary<string> }) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { logout } = useAzureAuthenticator();
  const { firstName = '', lastName = '', maidenName = null } = userName || {};
  const { isAvailabilityEnabled, user, setStatus } = useAvailabilityCurrentUser();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);

  const isOpen = Boolean(anchorEl);
  const currentStatus = user?.status || StatusType.OFFLINE;
  const currentStatusConfig = statusConfig[currentStatus];
  const isAvailabilityUiEnabled = isAvailabilityEnabled && isFeatureEnabled(Features.INTERRUPTIONS);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setStatusAnchorEl(null);
    setIsStatusMenuOpen(false);
  };

  const handleStatusMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
    setIsStatusMenuOpen(current => !current);
  };

  const handleSelectStatus = (status: StatusType) => {
    void setStatus(status);
    handleClose();
  };

  return userName === null ? null : (
    <>
      <IconButton size="large" aria-label="Open user menu" onClick={handleOpen}>
        <CurrentUserAvatarField statusIcon={isAvailabilityUiEnabled ? currentStatusConfig.icon : undefined} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={MENU_SLOT_PROPS}
      >
        <Typography
          sx={{
            pointerEvents: 'none',
            padding: '10px 16px',
            background: (theme: any) => theme.vars.palette.background.primary,
          }}
          variant="subtitle1"
        >
          {`Welcome, ${firstName} ${lastName}`}
          {maidenName ? ` (${maidenName})` : ''}
        </Typography>
        {isAvailabilityUiEnabled && (
          <MenuItem onClick={handleStatusMenuToggle} sx={MENU_ITEM_SX}>
            <ListItemIcon sx={MENU_ICON_SX}>
              <Box component="img" src={currentStatusConfig.icon} alt="" aria-hidden="true" sx={STATUS_ICON_SX} />
            </ListItemIcon>
            <ListItemText primary={currentStatusConfig.label} />
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </MenuItem>
        )}

        <MenuItem component={Link} to="/settings" sx={MENU_ITEM_SX}>
          <ListItemIcon sx={MENU_ICON_SX}>
            <Person2OutlinedIcon />
          </ListItemIcon>
          My profile
        </MenuItem>
        <MenuItem component={Link} to="/oauth/azure/logout" onClick={logout} sx={MENU_ITEM_SX}>
          <ListItemIcon sx={MENU_ICON_SX}>
            <LogoutIcon />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Popper
        open={isStatusMenuOpen}
        anchorEl={statusAnchorEl}
        placement="left-start"
        modifiers={STATUS_SUBMENU_MODIFIERS}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      >
        <Paper elevation={3} sx={STATUS_SUBMENU_PAPER_SX}>
          <MenuList dense sx={{ p: 0 }}>
            {orderedStatuses.map(status => {
              const itemStatusConfig = statusConfig[status];
              const isCurrent = status === currentStatus;

              return (
                <MenuItem
                  key={status}
                  selected={isCurrent}
                  onClick={() => handleSelectStatus(status)}
                  sx={MENU_ITEM_SX}
                >
                  <ListItemIcon sx={MENU_ICON_SX}>
                    <Box component="img" src={itemStatusConfig.icon} alt="" aria-hidden="true" sx={STATUS_ICON_SX} />
                  </ListItemIcon>
                  <ListItemText primary={itemStatusConfig.label} />
                  {isCurrent ? <CheckIcon sx={{ fontSize: 16 }} color="action" /> : null}
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
};

export default CurrentUserActions;
